import { Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Coins, Users, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import LiveRateTicker from "@/components/LiveRateTicker";

export default function Landing() {
  const { user } = useAuth();
  if (user) return <Navigate to="/home" replace />;

  return (
    <div className="min-h-screen bg-paper">
      <div className="mx-auto max-w-md px-6 pt-8 pb-16 relative z-10">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-full bg-gold-gradient flex items-center justify-center text-brand-page font-display text-xl font-bold shadow-gold">
              श
            </div>
            <span className="font-display text-2xl font-semibold text-brand-cream tracking-tight">ShreeDhan</span>
          </div>
          <Link to="/login" data-testid="landing-login-link" className="text-sm font-semibold text-brand-primary underline underline-offset-4">
            Sign in
          </Link>
        </div>

        {/* Hero */}
        <div className="mt-12 animate-fade-up">
          <div className="text-xs font-bold tracking-[0.28em] uppercase text-brand-primary">
            Ghar ka Sona · Digital ShreeDhan..
          </div>
          <h1 className="mt-5 font-display text-[3.2rem] leading-[1.02] font-semibold text-brand-cream tracking-tight">
            Invest or utilize gold<br />
            the way your <span className="italic text-gold-gradient whitespace-nowrap">"dadi-nani"</span> did —<br />
            <span className="text-gold-gradient">only Smarter.</span>
          </h1>
          <p className="mt-6 text-brand-slate text-[15px] leading-relaxed">
            Buy, save, gift, pledge and redeem 24k gold from your phone. BIS hallmarked, MMTC-PAMP vaulted. Trusted by Indian Shreedhani.
          </p>

          <div className="mt-7">
            <LiveRateTicker />
          </div>

          <div className="mt-8 flex flex-col gap-3">
            <Link to="/register" data-testid="landing-cta-register" className="w-full">
              <Button
                size="lg"
                className="w-full h-14 rounded-full bg-gold-gradient text-brand-page font-semibold text-base shadow-gold active:scale-[0.98] hover:opacity-95"
              >
                Start Saving in Gold <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/login" data-testid="landing-cta-login" className="w-full">
              <Button
                variant="ghost"
                className="w-full h-12 rounded-full text-brand-cream hover:bg-brand-sand hover:text-brand-primary"
              >
                I already have an account
              </Button>
            </Link>
          </div>
        </div>

        {/* Feature chips */}
        <div className="mt-12 grid grid-cols-3 gap-3">
          {[
            { Icon: ShieldCheck, label: "SEBI", sub: "regulated" },
            { Icon: Coins, label: "0.1g", sub: "min. buy" },
            { Icon: Users, label: "Family", sub: "Circle" },
          ].map(({ Icon, label, sub }, i) => (
            <div
              key={i}
              className="rounded-2xl border border-brand-line-faint bg-brand-sand p-4 text-center shadow-soft"
            >
              <Icon className="mx-auto text-brand-primary" size={22} />
              <div className="mt-2 font-semibold text-brand-cream">{label}</div>
              <div className="text-xs text-brand-slate">{sub}</div>
            </div>
          ))}
        </div>

        {/* Trust chip */}
        <div className="mt-6 rounded-2xl bg-brand-sand2 border border-brand-line-faint p-4 flex items-center gap-3 shadow-soft">
          <Sparkles className="text-brand-accent" size={20} />
          <div className="text-xs text-brand-slate leading-relaxed">
            <span className="font-semibold text-brand-cream">BIS Hallmarked</span> · MMTC-PAMP Vault · NSDL Audit · RBI KYC
          </div>
        </div>

        {/* Demo login */}
        <div className="mt-10 rounded-2xl bg-brand-sand2 border border-brand-line text-brand-cream p-5 shadow-card">
          <div className="text-xs uppercase tracking-[0.22em] text-brand-accent">Demo login</div>
          <div className="mt-2 font-semibold text-brand-cream">demo@shreedhan.app</div>
          <div className="text-sm text-brand-slate">Password: Demo@1234</div>
        </div>
      </div>
    </div>
  );
}
